import type { ReactNode } from 'react';
import { Navigate } from 'react-router';

import { useAccess } from '@/hooks/useAccess';
import { firstWebModulePath, routeModules, type Module } from '@/lib/modules';
import { StaffNotAuthorizedPage } from '@/pages/StaffNotAuthorizedPage';

export interface RequireModuleRouteProps {
  /** One module, or a set of which any one opens the route. */
  module: Module | readonly Module[];
  children: ReactNode;
}

/**
 * RequireModuleRoute renders its children for an admin or an employee who holds
 * `module` (issue #1670). Everyone else goes to the first granted web module, or
 * gets the `noWebAccess` lock-out when there is none. UX only; the server
 * module gate is the boundary.
 */
export function RequireModuleRoute({ module, children }: RequireModuleRouteProps) {
  const { isAdmin, hasModule } = useAccess();

  if (isAdmin || routeModules(module).some(hasModule)) {
    return <>{children}</>;
  }
  return <RedirectToFirstModule />;
}

/**
 * RedirectToFirstModule sends an employee to the first granted web module, or
 * shows the `noWebAccess` lock-out when there is none. Never redirects to `/`,
 * which would loop.
 */
export function RedirectToFirstModule() {
  const { modules } = useAccess();
  const fallback = firstWebModulePath(modules);
  if (fallback === null) {
    return <StaffNotAuthorizedPage reason="noWebAccess" />;
  }
  return <Navigate to={fallback} replace />;
}
